/**
 * Form Validation utilities for FormCrafter forms
 * Centralized validation logic and error formatting
 *
 * This file contains:
 * - Field validation (validateField) - validates single field values
 * - Error formatting (formatValidationError) - formats errors for display
 */

import { isValueEmpty } from './formDataUtils';
import { DECORATIVE_FIELD_TYPES } from '../../constants/formCrafter.constant';
import { isFileType } from './fieldTypeUtils';

/**
 * Validates a single field value against all field validations
 * Used by both form submission and inline field editing
 *
 * @param {*} value - The field value
 * @param {Object} field - The field configuration
 * @param {Function} t - Translation function
 * @returns {string|null} - Error message or null if valid
 */
export const validateField = (value, field, t) => {
  if (!field) return null;

  const fieldLabel = field.label || field.name || 'Field';
  const validations = field.validations || {};
  // Custom message from form creator - highest priority
  const customMessage = validations.customMessage;
  const pattern = validations.pattern;
  const minLength = validations.minLength;
  const maxLength = validations.maxLength;
  const minValue = validations.min;
  const maxValue = validations.max;
  const isRequired = field.required || validations.required;

  // Required validation
  if (isRequired && isValueEmpty(value)) {
    return (
      customMessage || t('formRenderer.fieldRequired', { field: fieldLabel })
    );
  }

  // Skip further validations if empty (not required)
  if (isValueEmpty(value)) return null;

  const strValue = String(value || '');

  // Email validation
  if (field.type === 'email') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(strValue)) {
      return customMessage || t('formRenderer.invalidEmail');
    }
  }

  // URL validation
  if (field.type === 'url') {
    try {
      new URL(strValue);
    } catch {
      return customMessage || t('formRenderer.invalidUrl');
    }
  }

  // Phone validation
  if (field.type === 'tel' || field.type === 'phone') {
    const phoneDigits = strValue.replace(/\D/g, '');
    if (phoneDigits.length < 7) {
      return customMessage || t('formRenderer.invalidPhone');
    }
  }

  // Pattern validation
  if (pattern) {
    try {
      let patternStr = pattern;
      if (patternStr.startsWith('/') && patternStr.lastIndexOf('/') > 0) {
        patternStr = patternStr.slice(1, patternStr.lastIndexOf('/'));
      }
      const regex = new RegExp(patternStr);
      if (!regex.test(strValue)) {
        return (
          customMessage ||
          t('formRenderer.patternMismatch', { field: fieldLabel })
        );
      }
    } catch (e) {
      console.warn('Invalid regex pattern:', pattern);
    }
  }

  // Min length validation
  if (minLength !== undefined && minLength !== null) {
    if (strValue.length < minLength) {
      return (
        customMessage ||
        t('formRenderer.minLength', { field: fieldLabel, min: minLength })
      );
    }
  }

  // Max length validation
  if (maxLength !== undefined && maxLength !== null) {
    if (strValue.length > maxLength) {
      return (
        customMessage ||
        t('formRenderer.maxLength', { field: fieldLabel, max: maxLength })
      );
    }
  }

  // Min value validation (for numbers)
  if (minValue !== undefined && minValue !== null && field.type === 'number') {
    const numValue = Number(value);
    if (!isNaN(numValue) && numValue < minValue) {
      return (
        customMessage ||
        t('formRenderer.minValue', { field: fieldLabel, min: minValue })
      );
    }
  }

  // Max value validation (for numbers)
  if (maxValue !== undefined && maxValue !== null && field.type === 'number') {
    const numValue = Number(value);
    if (!isNaN(numValue) && numValue > maxValue) {
      return (
        customMessage ||
        t('formRenderer.maxValue', { field: fieldLabel, max: maxValue })
      );
    }
  }

  return null;
};

/**
 * Recursively validates child fields within repeater rows.
 * Returns a flat error map with dot-notation keys: { "repeaterId[rowIndex].childId": "message" }
 *
 * @param {Array} value - The repeater value (array of row objects)
 * @param {Object} field - The repeater field definition (must have .fields for children)
 * @param {Function} t - Translation function
 * @param {string} [parentPath] - Path prefix for nested repeaters
 * @param {Object} [options] - Validation options
 * @param {boolean} [options.skipFileFields=true] - Skip file fields (default true for
 *   the main form flow where files are tracked externally; pass false for inline
 *   editing where the repeater manages file state locally)
 * @returns {Object} - Error map { [pathKey]: errorMessage }
 */
export const validateRepeaterChildren = (
  value,
  field,
  t,
  parentPath = '',
  options = {}
) => {
  if (!field || !Array.isArray(field.fields) || !Array.isArray(value))
    return {};
  console.log('validateRepeaterChildren', value, field, t, parentPath, options);

  const { skipFileFields = true } = options;
  const errors = {};
  const basePath = parentPath || field.id;

  value.forEach((row, rowIndex) => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return;

    field.fields.forEach((childField) => {
      if (
        DECORATIVE_FIELD_TYPES.includes(childField.type) ||
        childField.type === 'hidden'
      )
        return;

      // In the main form flow, file fields are managed externally (staged files,
      // server files, eager uploads) so the row value is unreliable.  In inline
      // editing (FieldEditor) the repeater manages files locally, so row data
      // IS reliable and file fields should be validated.
      if (skipFileFields && isFileType(childField.type)) return;

      const childValue = row[childField.id];
      const error = validateField(childValue, childField, t);

      if (error) {
        const errorKey = `${basePath}[${rowIndex}].${childField.id}`;
        errors[errorKey] = error;
      }

      if (childField.type === 'repeater' && Array.isArray(childValue)) {
        const nestedErrors = validateRepeaterChildren(
          childValue,
          childField,
          t,
          `${basePath}[${rowIndex}].${childField.id}`,
          options
        );
        Object.assign(errors, nestedErrors);
      }
    });
  });

  return errors;
};

/**
 * Gets the first error message from errors object
 * @param {Object} errors - Errors object { [subformId]: { [fieldId]: "message" } }
 * @returns {string|null} - First error message or null
 */
const getFirstError = (errors) => {
  if (!errors || typeof errors !== 'object') return null;

  for (const subformErrors of Object.values(errors)) {
    if (subformErrors && typeof subformErrors === 'object') {
      for (const message of Object.values(subformErrors)) {
        if (message && typeof message === 'string') {
          return message;
        }
      }
    }
  }
  return null;
};

/**
 * Formats validation error for display - shows ONE error at a time
 * @param {Object} errors - Errors object { [subformId]: { [fieldId]: "message" } }
 * @returns {string} - First error message to display
 */
export const formatValidationError = (errors) => {
  return getFirstError(errors) || '';
};
